import { useEffect, useState, useMemo } from 'react';
import { FilterTypes, SortTypes, FilterStatistics, FilterStatisticsResponse } from '../modules/sortAndFilter/workers/sortAndFilter';
import { filterAndSortWorkerFactory } from '../modules/sortAndFilter/workers/sortAndFilter/processing/processData.worker.factory';
import { statisticsWorkerFactory } from '../modules/sortAndFilter/workers/sortAndFilter/statistics/statistics.worker.factory';
import { Data } from '../modules/sortAndFilter/patterns/commands';
import { SortAndFilterConfig } from '../modules/sortAndFilter/config/SortAndFilterConfig';
import usePrevious from './usePrevious';
import localSortAndFilterConfig from '../modules/sortAndFilter/config/localSortAndFilterConfig.json';

export interface FilterAndSortTreatmentsResult {
    processedData: Data | null;
    processing: boolean;
    error: string | null;
    filters: FilterTypes[];
    statistics: {
        activeIngredient: FilterStatistics;
        brand: FilterStatistics;
        conditions: FilterStatistics;
        drugClass: FilterStatistics;
        price: FilterStatistics;
        review: FilterStatistics;
        treatmentForm: FilterStatistics;
    };
}

interface FilterTypesWithOrder extends FilterTypes {
    order: number;
}

interface Props {
    filterTypes: FilterTypes[];
    sortTypes: SortTypes[];
    data: Data;
    ordering?: SortAndFilterConfig;
    config: SortAndFilterConfig;
}

/**
 * Sort the available filters using the ordering rules provided.
 * If the user has selected filters these take precedence.
 * If there are URL Search Parameters for the filters and `filterTypes` is
 * not provided. These will be used. Otherwise an empty array will be used.
 * @param order the current ordering rules.
 * @param filterTypes the selected filters.
 * @param filtersParam the URL Search Parameter filters.
 * @returns FilterTypes[]
 */
export const sortFilters = (order: SortAndFilterConfig, filterTypes: FilterTypes[], previous: FilterTypes[]): FilterTypes[] => {
    let listOfFilters: FilterTypes[] = [];

    const reference: FilterTypes[] = filterTypes;
    let cloneOfUserInput: FilterTypes[] = [...filterTypes];

    if (JSON.stringify(reference) !== JSON.stringify(previous) && previous !== undefined) {
        cloneOfUserInput = reference;
    }

    listOfFilters = [...cloneOfUserInput];

    // Using the ordering rules set the order value to the provided filter.
    listOfFilters = Object.entries(order.filters)
        .map(([key, value]) => {
            let result: FilterTypesWithOrder | null = null;
            // Find the filter that matches the current order rule.
            const filterType = listOfFilters.find((type) => type.type === key);
            if (filterType) {
                // Assign the order rule value to the filter for further processing.
                result = { ...filterType, order: value.order } as FilterTypesWithOrder;
            }
            return result;
        })
        .filter((item): item is FilterTypesWithOrder => item !== null);

    // Return the filters in the ascending order. E.g. 1, 2, 3 or 3, 6, 7.
    return (listOfFilters as FilterTypesWithOrder[])
        .sort((a, b) => a.order - b.order)
        .map((filterType) => ({ type: filterType.type, keys: filterType.keys }));
};

const selectPreferredSort = (sortTypes: SortTypes[], previous: SortTypes[]) => {
    const reference: SortTypes[] = sortTypes;
    let cloneOfUserInput: SortTypes[] = [...sortTypes];

    if (JSON.stringify(reference) !== JSON.stringify(previous) && previous !== undefined) {
        cloneOfUserInput = reference;
    }

    // Return the final sortTypes
    return cloneOfUserInput;
};

/**
 * Filter and sort treatments data based on the selected filters, sorts, and ordering selected by the user.
 * When the URL contains Search Parameters and no filters, sorts, ordering has been selected, they will be used.
 * @param {Object} {filterTypes: FilterTypes[], sortTypes: SortTypes[], data: Data, ordering?: SortAndFilterConfig} The hook parameters.
 * @returns FilterAndSortTreatmentsResult
 */
export const useFilterAndSortTreatments = ({ filterTypes, sortTypes, data, ordering, config }: Props): FilterAndSortTreatmentsResult => {
    const [processedData, setProcessedData] = useState<Data | []>(data);
    const [statistics, setStatisticsData] = useState<FilterStatisticsResponse>({
        activeIngredient: {
            total: 0,
            statistics: [],
        },
        brand: {
            total: 0,
            statistics: [],
        },
        conditions: {
            total: 0,
            statistics: [],
        },
        drugClass: {
            total: 0,
            statistics: [],
        },
        treatmentForm: {
            total: 0,
            statistics: [],
        },
        price: {
            total: 0,
            statistics: [],
        },
        review: {
            total: 0,
            statistics: [],
        },
    });
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<FilterTypes[]>(filterTypes);
    const [sorts, setSorts] = useState<SortTypes[]>(sortTypes);
    const [order, setOrder] = useState<SortAndFilterConfig>(ordering ?? (localSortAndFilterConfig as SortAndFilterConfig));

    const processDataWorker = useMemo(() => filterAndSortWorkerFactory(), []);
    const statisticsDataWorker = useMemo(() => statisticsWorkerFactory(), []);

    const previousFilter = usePrevious(filterTypes) as unknown as FilterTypes[];
    const previousSort = usePrevious(sortTypes) as unknown as SortTypes[];

    useEffect(() => {
        (async () => {
            setProcessing(true);

            // Send any filters selected by the user, and URL Search Parameters if available.
            const initialFilters: FilterTypes[] = sortFilters(order, filterTypes, previousFilter);

            // Use sorts selected by the user or the URL Search Parameters if available.
            const initialSorts: SortTypes[] = selectPreferredSort(sortTypes, previousSort);

            try {
                const result = await processDataWorker(initialFilters, initialSorts, data);
                const statisticsResult = await statisticsDataWorker(initialFilters, result as Data, config);
                setProcessedData(result as Data);
                setStatisticsData(statisticsResult as FilterStatisticsResponse);
                setError(null);
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                }
                // eslint-disable-next-line no-console
                console.error(err);
            } finally {
                setProcessing(false);
                setFilters(initialFilters);
                setSorts(initialSorts);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filterTypes), JSON.stringify(sortTypes), JSON.stringify(sorts), JSON.stringify(data)]);

    const values = useMemo(
        () => ({
            processedData,
            processing,
            error,
            filters,
            statistics,
        }),
        [processedData, processing, error, filters, statistics]
    );

    return values;
};
